import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Table, Select, InputNumber, Checkbox, Button, EmptyState, Tag, Tooltip, message, Drawer } from '../../ui';
import { request } from '../../api/client';

const inr = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const inr2 = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Recent Indian financial years (Apr–Mar), most recent first.
const FY_OPTIONS = (() => {
  const d = new Date();
  const startYr = (d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1);
  return [0, 1, 2].map((b) => { const s = startYr - b; return `${s}-${String((s + 1) % 100).padStart(2, '0')}`; });
})();

type Sel = Record<string, { checked: boolean; amount: number }>;

/**
 * Year-end TOD run. Loads the per-retailer suggestion (net turnover × the FY scheme's §4 TOD %, less already-
 * granted), lets Accounts tick + edit the amounts, then drafts the selected ones as turnover bonuses — each
 * of which then goes through the normal Admin approval. Compute-only until "Draft" is pressed.
 */
export function TodRunDrawer({ open, onClose, onGenerated }: { open: boolean; onClose: () => void; onGenerated: () => void }) {
  const qc = useQueryClient();
  const [fy, setFy] = useState<string | undefined>();
  const [sel, setSel] = useState<Sel>({});

  const { data, isLoading } = useQuery({
    queryKey: ['tod-preview', fy ?? null],
    queryFn: () => request<any>({ url: '/credit-notes/tod-preview', params: fy ? { financialYear: fy } : {} }),
    enabled: open,
  });
  const rows: any[] = data?.rows ?? [];
  const resolvedFy: string | null = data?.financialYear ?? fy ?? null;

  // (Re)initialise the tick + amount whenever a fresh preview arrives (open, or FY changed).
  useEffect(() => {
    if (!data) return;
    const init: Sel = {};
    for (const r of rows) init[r.retailerId] = { checked: r.suggestedBonus > 0, amount: r.suggestedBonus };
    setSel(init);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const setRow = (rid: string, patch: Partial<{ checked: boolean; amount: number }>) =>
    setSel((p) => ({ ...p, [rid]: { checked: p[rid]?.checked ?? false, amount: p[rid]?.amount ?? 0, ...patch } }));
  const toggleAll = (checked: boolean) =>
    setSel((p) => { const next: Sel = { ...p }; for (const r of rows) next[r.retailerId] = { checked, amount: p[r.retailerId]?.amount ?? r.suggestedBonus }; return next; });

  const selected = useMemo(() => rows.filter((r) => sel[r.retailerId]?.checked && (sel[r.retailerId]?.amount ?? 0) > 0), [rows, sel]);
  const total = useMemo(() => selected.reduce((s, r) => s + (sel[r.retailerId]?.amount ?? 0), 0), [selected, sel]);
  const allChecked = rows.length > 0 && rows.every((r) => sel[r.retailerId]?.checked);
  const someChecked = rows.some((r) => sel[r.retailerId]?.checked);

  const gen = useMutation({
    mutationFn: () => request<any>({
      url: '/credit-notes/tod-generate', method: 'POST',
      data: { financialYear: resolvedFy, items: selected.map((r) => ({ retailerId: r.retailerId, amount: sel[r.retailerId].amount })) },
    }),
    onSuccess: (res: any) => {
      const n = res?.createdCount ?? 0;
      const skipped = res?.skippedCount ?? 0;
      const failed = res?.errors?.length ?? 0;
      // A fresh preview must reflect what was just drafted (already-granted → suggested drops), so a re-open
      // can't re-draft the same bonuses off a stale cache.
      qc.invalidateQueries({ queryKey: ['tod-preview'] });
      if (n === 0) {
        message.warning(
          skipped ? `Nothing drafted — ${skipped} retailer${skipped === 1 ? ' already has' : 's already have'} a TOD bonus this year`
            : failed ? `Nothing drafted — all ${failed} failed`
            : 'Nothing was drafted',
        );
        return; // keep the drawer open so the edited selection isn't lost
      }
      message.success(`Drafted ${n} turnover bonus${n === 1 ? '' : 'es'}${skipped ? ` · ${skipped} skipped (already granted)` : ''}${failed ? ` · ${failed} failed` : ''} — an Admin can now approve them`);
      onGenerated();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Could not draft the TOD bonuses'),
  });

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={860}
      title="Run turnover discount (TOD)"
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <div className="text-sm text-muted">
            <span className="font-semibold text-content">{selected.length}</span> selected · <span className="font-semibold text-content">{inr2(total)}</span>
          </div>
          <div className="flex gap-2">
            <Button variant="default" onClick={onClose}>Cancel</Button>
            <Button variant="primary" disabled={!selected.length} loading={gen.isPending} onClick={() => gen.mutate()}>
              Draft {selected.length} bonus{selected.length === 1 ? '' : 'es'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div>
          <div className="mb-1 text-xs font-medium text-muted">Financial year</div>
          <Select style={{ width: 160 }} value={resolvedFy ?? undefined} onChange={(v) => setFy(v)} options={FY_OPTIONS.map((o) => ({ value: o, label: o }))} />
        </div>
        {data?.from && <div className="text-xs text-subtle">Turnover window {String(data.from)} → {String(data.to)}</div>}
      </div>

      <p className="mb-3 text-sm text-muted">
        Suggested bonus = <span className="font-medium">net turnover × the scheme's TOD %</span> (per §4 slabs), less any TOD already
        granted this year. Tick + adjust, then draft — each becomes a turnover bonus awaiting an Admin's approval. No money moves here.
      </p>

      {data?.note && !rows.length ? (
        <EmptyState title={data.note} />
      ) : (
        <Table
          rowKey="retailerId"
          size="small"
          loading={isLoading}
          dataSource={rows}
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'No eligible retailers with turnover for this year' }}
          columns={[
            {
              title: <Checkbox checked={allChecked ? true : someChecked ? 'indeterminate' : false} onCheckedChange={(checked) => toggleAll(checked)} />,
              key: 'sel', width: 44,
              render: (_: any, r: any) => <Checkbox checked={!!sel[r.retailerId]?.checked} onCheckedChange={(checked) => setRow(r.retailerId, { checked })} />,
            },
            { title: 'Retailer', dataIndex: 'retailerName', render: (v: string, r: any) => <span>{r.retailerCode ? <span className="text-subtle">{r.retailerCode} · </span> : ''}{v ?? `#${r.retailerId}`}</span> },
            { title: 'Net turnover', dataIndex: 'netTurnover', align: 'right' as const, width: 130, render: (v: any, r: any) => (
              // Show the clean NET business; the gross/returns breakdown lives in a hover tooltip (auditable but not cluttering).
              r.returnsTotal > 0
                ? <Tooltip label={`Gross ${inr(r.grossTurnover)} − returns ${inr(r.returnsTotal)}`}><span className="cursor-help underline decoration-dotted underline-offset-2">{inr(v)}</span></Tooltip>
                : inr(v)
            ) },
            { title: 'TOD %', dataIndex: 'todPct', align: 'right' as const, width: 80, render: (v: any) => <Tag tone="blue">{v}%</Tag> },
            { title: 'Computed', dataIndex: 'computedBonus', align: 'right' as const, width: 120, render: (v: any) => inr2(v) },
            { title: 'Granted', dataIndex: 'alreadyGranted', align: 'right' as const, width: 110, render: (v: any) => (Number(v) > 0 ? <span className="text-subtle">{inr2(v)}</span> : <span className="text-subtle">—</span>) },
            {
              title: 'Bonus to draft', key: 'amount', align: 'right' as const, width: 160,
              render: (_: any, r: any) => (
                <InputNumber
                  size="small" min={0} style={{ width: 140 }} disabled={!sel[r.retailerId]?.checked}
                  value={sel[r.retailerId]?.amount ?? 0} onChange={(v) => setRow(r.retailerId, { amount: Number(v ?? 0) })}
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={(v) => Number((v || '').replace(/,/g, ''))}
                />
              ),
            },
          ]}
        />
      )}
    </Drawer>
  );
}
